import { HeadlessUI } from '@systemeio/ui'
import { Fragment, useCallback, useEffect, useState } from 'react'
import { usePagination } from 'shared/route-query/hooks/use-pagination'
import { twMerge } from 'tailwind-merge'
import { useNotifications } from '../hooks/use-notifications'
import UnreadIndicator from './unread-indicator'
import { useOpenNotificationMenuQuery } from '../hooks/use-open-notification-menu-query'
import { BellIcon } from '@systemeio/ui'
import { useActualHeaderLayoutType } from 'shared/components/header'
import { NotificationsMenuContent } from './notifications-menu-content'

export interface NotificationMenuProps {
  className?: string
}

const defaultFilter = {
  onlyUnRead: false,
}

export const NotificationMenuMobile = ({ className }: NotificationMenuProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const toggleMenuState = useCallback(() => {
    setIsMenuOpen(prevState => !prevState)
  }, [])

  const { pagination } = usePagination(true)
  const { notificationsMeta } = useNotifications({
    shouldFetchNotifications: false,
    limit: 3,
    pagination,
    filter: defaultFilter,
  })

  const { isDesktop } = useActualHeaderLayoutType()
  const { openThroughQuery } = useOpenNotificationMenuQuery({
    allowCheck: isDesktop === false,
  })

  useEffect(() => {
    if (openThroughQuery) {
      setIsMenuOpen(true)
    }
  }, [openThroughQuery])

  return (
    <>
      <button
        onClick={toggleMenuState}
        className="focus-visible:border-brand-primary focus:outline-hidden flex h-full items-center justify-around gap-2.5 border-b-4 border-transparent"
      >
        <div className="text-background-secondary/90 relative w-5 text-sm font-bold">
          <BellIcon
            className={twMerge(
              'fill-text-secondary stroke-text-secondary hover:fill-background-secondary hover:stroke-background-secondary transition',
              isMenuOpen && 'fill-background-secondary stroke-background-secondary',
            )}
          />
          {notificationsMeta?.hasUnread && (
            <UnreadIndicator className="absolute right-0 top-0 p-0" />
          )}
        </div>
      </button>
      <HeadlessUI.Transition show={isMenuOpen} as={Fragment}>
        <HeadlessUI.Dialog open={isMenuOpen} onClose={toggleMenuState}>
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex w-full pt-[69px]">
              <HeadlessUI.TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <HeadlessUI.DialogPanel
                  className={twMerge(
                    'bg-background-secondary relative w-full transform text-left shadow-xl transition-all',
                    className,
                  )}
                >
                  <NotificationsMenuContent />
                </HeadlessUI.DialogPanel>
              </HeadlessUI.TransitionChild>
            </div>
          </div>
        </HeadlessUI.Dialog>
      </HeadlessUI.Transition>
    </>
  )
}
